import { useCallback, useEffect, useState } from 'react';

export type Density = 'compact' | 'comfortable';

const STORAGE_KEY = 'aurora_ui_density';
const EVENT = 'aurora:density-change';

function readInitial(): Density {
  if (typeof window === 'undefined') return 'comfortable';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'compact' || v === 'comfortable' ? v : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

// Task #1665 — hook coerente per density toggle (Chat + Feed).
// Persistito in localStorage + sincronizzato cross-componente via custom event.
// (user_settings.ui_density: integrazione backend rinviata a follow-up — qui
// FE-only come da scope "task puramente FE".)
export function useDensity(): {
  density: Density;
  setDensity: (d: Density) => void;
  toggle: () => void;
  cls: (compact: string, comfortable: string) => string;
} {
  const [density, setDensityState] = useState<Density>(readInitial);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<Density>;
      if (ce.detail === 'compact' || ce.detail === 'comfortable') {
        setDensityState(ce.detail);
      }
    };
    window.addEventListener(EVENT, handler as EventListener);
    return () => window.removeEventListener(EVENT, handler as EventListener);
  }, []);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    try { localStorage.setItem(STORAGE_KEY, d); } catch { /* noop */ }
    try { window.dispatchEvent(new CustomEvent<Density>(EVENT, { detail: d })); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setDensity(density === 'compact' ? 'comfortable' : 'compact');
  }, [density, setDensity]);

  const cls = useCallback(
    (compact: string, comfortable: string) => (density === 'compact' ? compact : comfortable),
    [density],
  );

  return { density, setDensity, toggle, cls };
}
